/**
 * Per-project field configuration for the Company step.
 *
 * Why this file exists:
 *  Each project schema stores company info differently:
 *    - leaveHub.companies   → flat: name, hr_email, address, tax_number
 *    - Rentaru_Rental.Company → minimal: name (TH+EN), num_org, contact
 *    - smart_law.Company      → same minimal shape as Rentaru
 *
 *  The form in CompanyStep is ONE shared component. It reads this config to
 *  decide which fields to render and what to label them.
 */

import { PROJECTS } from "./projectConfig";

// Canonical field IDs. Backend mapping uses these — DO NOT rename without
// updating projects/*.js too.
//
// Note: TAX_NUMBER moved to BillingSection (hardcoded in CompanyStep) because
//       its label/validation depends on detail_type — doesn't fit flat config.
export const FIELD = {
  NAME_TH: "name_th",
  NAME_EN: "name_en",
  EMAIL: "email",
  PHONE: "phone",
  CONTACT_PERSON: "contact_person",
  ADDRESS: "address",
  NUM_ORGANIZATION: "num_organization",
};

export const FIELD_META = {
  [FIELD.NAME_TH]: {
    label: { th: "ชื่อบริษัท", en: "Company name" },
    placeholder: { th: "บริษัท ตัวอย่าง จำกัด", en: "Example Co., Ltd." },
    required: true,
    type: "text",
  },
  [FIELD.NAME_EN]: {
    label: { th: "ชื่อบริษัท (อังกฤษ)", en: "Company name (English)" },
    placeholder: { th: "Example Co., Ltd.", en: "Example Co., Ltd." },
    required: false,
    type: "text",
  },
  [FIELD.EMAIL]: {
    label: { th: "อีเมลบริษัท", en: "Company email" },
    placeholder: { th: "contact@company.com", en: "contact@company.com" },
    required: true,
    type: "email",
  },
  [FIELD.PHONE]: {
    label: { th: "เบอร์โทร", en: "Phone" },
    placeholder: { th: "02-xxx-xxxx", en: "02-xxx-xxxx" },
    required: true,
    type: "tel",
  },
  [FIELD.CONTACT_PERSON]: {
    label: { th: "ผู้ติดต่อ", en: "Contact person" },
    placeholder: { th: "ชื่อ-นามสกุล", en: "Full name" },
    required: false,
    type: "text",
  },
  [FIELD.ADDRESS]: {
    label: { th: "ที่อยู่", en: "Address" },
    placeholder: { th: "เลขที่ ถนน แขวง เขต จังหวัด", en: "Full address" },
    required: false,
    type: "textarea",
  },
  [FIELD.NUM_ORGANIZATION]: {
    label: { th: "จำนวนสาขา", en: "Number of branches" },
    placeholder: { th: "1", en: "1" },
    required: false,
    type: "number",
  },
};

export const PROJECT_FIELDS = {
  [PROJECTS.LEAVE_HUB]: [
    FIELD.NAME_TH,
    FIELD.EMAIL,
    FIELD.PHONE,
    FIELD.CONTACT_PERSON,
    FIELD.ADDRESS,
  ],
  [PROJECTS.RENTARU]: [
    FIELD.NAME_TH,
    // FIELD.NAME_EN,
    FIELD.EMAIL,
    FIELD.PHONE,
    FIELD.CONTACT_PERSON,
    // FIELD.NUM_ORGANIZATION,
  ],
  [PROJECTS.SMART_LAW]: [
    FIELD.NAME_TH,
    // FIELD.NAME_EN,
    FIELD.EMAIL,
    FIELD.PHONE,
    FIELD.CONTACT_PERSON,
    // FIELD.NUM_ORGANIZATION,
  ],
};

const PROJECTS_WITH_BILLING = new Set([
  PROJECTS.LEAVE_HUB,
  PROJECTS.RENTARU,
  PROJECTS.SMART_LAW,
]);

// ─── Public helpers ──────────────────────────────────────────

export const getFieldsForProject = (projectId) => {
  return PROJECT_FIELDS[Number(projectId)] ?? PROJECT_FIELDS[PROJECTS.LEAVE_HUB];
};

export const getFieldMeta = (fieldId) => {
  return FIELD_META[fieldId] ?? null;
};

export const projectSupportsBilling = (projectId) => {
  return PROJECTS_WITH_BILLING.has(Number(projectId));
};